/**
 * Report definitions — each report is a config object consumed by the ReportDetail template.
 * No separate component per report; the template renders based on this config.
 *
 * Per REPORT_DETAIL_SPECS.md:
 * - A1–A4: Overview drill-downs
 * - B1–B4, B6, B7: Library reports (B5 separate story, B8–B9 deferred)
 *
 * LAT-25: Role visibility
 * - 'admin' reports: visible to admin/owner only (financial reports with margin data)
 * - 'field' reports: visible to all users
 * When no role system is active, all reports are shown (admin default).
 */

// REQ-RPT-01: shared conversion / win-rate metric (accepted/converted/won)
import { conversionCount, conversionRate } from '../../utils/conversionMetrics';
import { quoteDateValue } from '../../utils/normaliseSalespeople';
import { deriveJobStatus } from '../../utils/jobStatus';

// IDI-779: the job reports read the Job page's status (D1). ReportDetail runs
// preFilter before the Status filter and computeRow, so the filter, column,
// chart and tiles all read the derived value. The range (IDI-777) runs first
// and still reads the stored status for the Job Performance date.
const withDerivedJobStatus = (data) => data.map(d => ({ ...d, status: deriveJobStatus(d) }));
// D2: Active is every job on the report that isn't Completed.
const countActiveJobs = (data) => data.filter(d => d.status !== 'completed').length;

export const REPORT_DEFINITIONS = {
  // ─── A1: Margin Analysis (Overview drill-down) ───────────────────────────────
  margin_analysis: {
    id: 'margin_analysis',
    title: 'Margin Analysis',
    description: 'Analyze profit margins by category. Flags jobs below 5% threshold.',
    category: 'financial',
    visibility: 'admin', // LAT-25: admin/owner only
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'job_type', type: 'text', label: 'Job Type' },
      { key: 'salesperson', type: 'text', label: 'Salesperson' },
    ],
    kpis: [
      { key: 'overall_margin', label: 'Overall Margin %', format: 'percent' },
      { key: 'revenue', label: 'Total Revenue', format: 'currency' },
      { key: 'cost', label: 'Total Cost', format: 'currency' },
      { key: 'below_threshold', label: 'Jobs Below 5%', format: 'number' },
    ],
    columns: [
      { key: 'category', label: 'Category', sortable: true },
      { key: 'job_count', label: 'Jobs', sortable: true, align: 'right' },
      { key: 'revenue', label: 'Revenue', sortable: true, align: 'right', format: 'currency' },
      { key: 'cost', label: 'Cost', sortable: true, align: 'right', format: 'currency' },
      { key: 'margin_dollars', label: 'Margin $', sortable: true, align: 'right', format: 'currency' },
      { key: 'margin_pct', label: 'Margin %', sortable: true, align: 'right', format: 'percent_flagged' },
    ],
    dataSource: 'jobs',
    chartType: 'bar',
    chartConfig: {
      groupBy: 'type',
      groupField: 'job_type',
      valueField: 'revenue',
      xKey: 'label',
      yKey: 'value',
      yLabel: 'Revenue',
      yFormat: 'currency',
    },
    computeKpis: (data) => {
      const revenue = data.reduce((sum, d) => sum + (d.revenue || d.total || 0), 0);
      const cost = data.reduce((sum, d) => sum + (d.cost || 0), 0);
      const profit = revenue - cost;
      const belowThreshold = data.filter(d => {
        const r = d.revenue || d.total || 0;
        const c = d.cost || 0;
        return r > 0 && ((r - c) / r) * 100 < 5;
      }).length;
      return {
        overall_margin: revenue > 0 ? Math.round((profit / revenue) * 100) : 0,
        revenue,
        cost,
        below_threshold: belowThreshold,
      };
    },
    computeRow: (item) => {
      const revenue = item.revenue || item.total || 0;
      const cost = item.cost || 0;
      const margin = revenue > 0 ? Math.round(((revenue - cost) / revenue) * 100) : 0;
      return {
        ...item,
        category: item.category || item.job_type || 'Uncategorized',
        job_count: 1,
        margin_dollars: revenue - cost,
        margin_pct: margin,
      };
    },
  },

  // ─── A2: Job Performance (Overview drill-down) ───────────────────────────────
  job_performance: {
    id: 'job_performance',
    title: 'Job Performance',
    description: 'Track active/completed jobs, change orders, and average job value.',
    category: 'financial',
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'status', type: 'select', label: 'Status', match: 'exact', options: ['all', 'not_scheduled', 'partially_scheduled', 'scheduled', 'completed'] },
      { key: 'job_type', type: 'text', label: 'Job Type' },
      { key: 'salesperson', type: 'text', label: 'Salesperson' },
    ],
    kpis: [
      { key: 'active_jobs', label: 'Active Jobs', format: 'number' },
      { key: 'completed_jobs', label: 'Completed Jobs', format: 'number' },
      { key: 'change_orders', label: 'Change Orders $', format: 'currency' },
      { key: 'avg_job_value', label: 'Avg Job Value', format: 'currency' },
    ],
    columns: [
      { key: 'title', label: 'Job', sortable: true },
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'status', label: 'Status', sortable: true, format: 'status' },
      { key: 'total', label: 'Job $', sortable: true, align: 'right', format: 'currency' },
      { key: 'margin_pct', label: 'Margin %', sortable: true, align: 'right', format: 'percent' },
      { key: 'completed_date', label: 'Completed', sortable: true, format: 'date' },
    ],
    dataSource: 'jobs',
    preFilter: withDerivedJobStatus,
    chartType: 'line',
    chartConfig: {
      groupBy: 'month',
      xKey: 'label',
      yKey: 'value',
      yLabel: 'Jobs',
      yFormat: 'number',
    },
    computeKpis: (data) => {
      const active = countActiveJobs(data);
      const completed = data.filter(d => d.status === 'completed').length;
      const totalValue = data.reduce((sum, d) => sum + (d.total || d.revenue || 0), 0);
      const changeOrders = data.reduce((sum, d) => sum + (d.change_order_amount || 0), 0);
      return {
        active_jobs: active,
        completed_jobs: completed,
        change_orders: changeOrders,
        avg_job_value: data.length > 0 ? Math.round(totalValue / data.length) : 0,
      };
    },
    computeRow: (item) => {
      const revenue = item.revenue || item.total || 0;
      const cost = item.cost || 0;
      return {
        ...item,
        total: revenue,
        margin_pct: revenue > 0 ? Math.round(((revenue - cost) / revenue) * 100) : 0,
        // IDI-246: complete_job stores completedDate (the dealer's day) and
        // completedAt (camelCase, from /v1/jobs); the day reads first so a
        // late-evening completion never shows as tomorrow.
        completed_date: item.completedDate || item.completed_date || item.completedAt || item.completed_at,
      };
    },
  },

  // ─── A3: Revenue (Overview drill-down) ───────────────────────────────────────
  revenue: {
    id: 'revenue',
    title: 'Revenue',
    description: 'Revenue breakdown: invoiced, paid, outstanding, ready to invoice.',
    category: 'financial',
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'status', type: 'select', label: 'Payment Status', options: ['all', 'paid', 'sent', 'draft'] },
      { key: 'customer_name', type: 'text', label: 'Customer' },
    ],
    kpis: [
      { key: 'total_invoiced', label: 'Total Invoiced', format: 'currency' },
      { key: 'paid', label: 'Paid', format: 'currency' },
      { key: 'outstanding', label: 'Outstanding', format: 'currency' },
      { key: 'ready_to_invoice', label: 'Ready to Invoice', format: 'currency' },
    ],
    columns: [
      { key: 'invoice_number', label: 'Invoice #', sortable: true },
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'amount', label: 'Amount', sortable: true, align: 'right', format: 'currency' },
      { key: 'status', label: 'Status', sortable: true, format: 'status' },
      { key: 'due_date', label: 'Due Date', sortable: true, format: 'date' },
      { key: 'paid_date', label: 'Paid Date', sortable: true, format: 'date' },
    ],
    dataSource: 'invoices',
    chartType: 'area',
    chartConfig: {
      groupBy: 'month',
      valueField: 'total',
      xKey: 'label',
      yKey: 'value',
      yLabel: 'Revenue',
      yFormat: 'currency',
    },
    // IDI-395: the invoices API (GET /v1/invoices) returns camelCase fields
    // — invoiceNumber, customerName, totalAmount, dueDate, paidDate. This
    // report's columns use snake_case keys (invoice_number, customer_name,
    // due_date, paid_date) and computeRow read item.total||item.amount,
    // neither of which exists on a real invoice. `status` happened to match
    // (snake_case === camelCase for a single word), which is exactly why the
    // bug presented as "row count right, status renders, every other field
    // blank" — the KPI totals and every currency/date column silently summed
    // to 0 / rendered empty. Map every real field explicitly here so
    // computeRow's output actually has the keys the columns above expect.
    computeKpis: (data) => {
      const amt = (d) => Number(d.totalAmount ?? d.total ?? d.amount ?? 0);
      const total = data.reduce((sum, d) => sum + amt(d), 0);
      const paid = data.filter(d => d.status === 'paid').reduce((sum, d) => sum + amt(d), 0);
      const outstanding = data.filter(d => d.status === 'sent' || d.status === 'viewed').reduce((sum, d) => sum + amt(d), 0);
      const readyToInvoice = data.filter(d => d.status === 'draft').reduce((sum, d) => sum + amt(d), 0);
      return { total_invoiced: total, paid, outstanding, ready_to_invoice: readyToInvoice };
    },
    computeRow: (item) => ({
      ...item,
      invoice_number: item.invoiceNumber || item.invoice_number || '',
      customer_name: item.customerName || item.customer_name || '',
      amount: Number(item.totalAmount ?? item.total ?? item.amount ?? 0),
      due_date: item.dueDate || item.due_date || null,
      paid_date: item.paidDate || item.paid_date || null,
    }),
  },

  // ─── A4: Quote Performance (Overview drill-down) ─────────────────────────────
  quote_performance: {
    id: 'quote_performance',
    title: 'Quote Performance',
    description: 'Quote funnel: total → accepted → converted to job, by salesperson.',
    category: 'sales',
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'salesperson', type: 'text', label: 'Salesperson' },
      { key: 'status', type: 'select', label: 'Status', options: ['all', 'draft', 'sent', 'accepted', 'declined'] },
    ],
    kpis: [
      { key: 'total_quotes', label: 'Total Quotes', format: 'number' },
      { key: 'accepted', label: 'Accepted', format: 'number' },
      { key: 'converted', label: 'Converted to Job', format: 'number' },
      { key: 'win_rate', label: 'Win Rate %', format: 'percent' },
    ],
    columns: [
      { key: 'salesperson', label: 'Salesperson', sortable: true },
      { key: 'quote_number', label: 'Quote #', sortable: true },
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'amount', label: 'Quote $', sortable: true, align: 'right', format: 'currency' },
      { key: 'status', label: 'Status', sortable: true, format: 'status' },
      { key: 'created_at', label: 'Created', sortable: true, format: 'date' },
    ],
    dataSource: 'quotes',
    chartType: 'bar',
    chartConfig: {
      groupBy: 'month',
      xKey: 'label',
      yKey: 'value',
      yLabel: 'Quotes',
      yFormat: 'number',
    },
    computeKpis: (data) => {
      const total = data.length;
      const accepted = conversionCount(data);
      const converted = data.filter(d => d.converted_to_job_id).length;
      return {
        total_quotes: total,
        accepted,
        converted,
        win_rate: conversionRate(data),
      };
    },
    computeRow: (item) => ({
      ...item,
      amount: item.total || item.amount || 0,
      created_at: quoteDateValue(item), // IDI-777: the Quote Date; the API sends camelCase
      salesperson: item.salesperson || item.created_by || '—',
    }),
  },

  // ─── B1: Bid Log (Library — showcase report) ─────────────────────────────────
  bid_log: {
    id: 'bid_log',
    title: 'Bid Log',
    description: 'Full bid log: all quotes by month with annual summary KPIs.',
    category: 'sales',
    groupBy: 'created_at', // LAT-20: group by month
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'salesperson', type: 'text', label: 'Salesperson' },
      { key: 'status', type: 'select', label: 'Status', options: ['all', 'draft', 'sent', 'accepted', 'declined', 'expired'] },
      { key: 'job_type', type: 'text', label: 'Job Type' },
      { key: 'customer_name', type: 'text', label: 'Customer' },
    ],
    kpis: [
      { key: 'total_quotes', label: '# Quotes', format: 'number' },
      { key: 'avg_quote', label: 'Avg Quote $', format: 'currency' },
      { key: 'total_value', label: 'Total Quote $', format: 'currency' },
      { key: 'total_jobs', label: '# Jobs Won', format: 'number' },
    ],
    columns: [
      { key: 'created_at', label: 'Created', sortable: true, format: 'date' },
      { key: 'salesperson', label: 'Salesperson', sortable: true },
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'title', label: 'Project Name', sortable: true },
      { key: 'amount', label: 'Job $', sortable: true, align: 'right', format: 'currency' },
      { key: 'margin_pct', label: 'Margin %', sortable: true, align: 'right', format: 'percent' },
      { key: 'job_type', label: 'Job Type', sortable: true },
      { key: 'status', label: 'Status', sortable: true, format: 'status' },
    ],
    dataSource: 'quotes',
    chartType: 'bar',
    chartConfig: {
      groupBy: 'month',
      xKey: 'label',
      yKey: 'value',
      yLabel: 'Bids',
      yFormat: 'number',
    },
    computeKpis: (data) => {
      const total = data.length;
      const totalValue = data.reduce((sum, d) => sum + (d.total || d.amount || 0), 0);
      // REQ-RPT-01: shared metric (counts accepted/converted/won/job-linked)
      const won = conversionCount(data);
      return {
        total_quotes: total,
        avg_quote: total > 0 ? Math.round(totalValue / total) : 0,
        total_value: totalValue,
        total_jobs: won,
      };
    },
    computeRow: (item) => {
      const revenue = item.total || item.amount || 0;
      const cost = item.cost || 0;
      return {
        ...item,
        amount: revenue,
        created_at: quoteDateValue(item), // IDI-777: the Quote Date; the API sends camelCase
        salesperson: item.salesperson || item.created_by || '—',
        margin_pct: revenue > 0 ? Math.round(((revenue - cost) / revenue) * 100) : 0,
        job_type: item.job_type || item.category || '—',
      };
    },
  },

  // ─── B2: Customer Quote & Contact Report ─────────────────────────────────────
  customer_quote_contact: {
    id: 'customer_quote_contact',
    title: 'Customer Quote & Contact',
    description: 'Customer quotes and contact details in one view.',
    category: 'sales',
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'customer_name', type: 'text', label: 'Customer' },
    ],
    kpis: [
      { key: 'total_customers', label: 'Customers', format: 'number' },
      { key: 'total_quotes', label: 'Quotes', format: 'number' },
      { key: 'total_value', label: 'Total Value', format: 'currency' },
      { key: 'avg_quote', label: 'Avg Quote', format: 'currency' },
    ],
    columns: [
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'title', label: 'Project Name', sortable: true },
      { key: 'amount', label: 'Quote Amount', sortable: true, align: 'right', format: 'currency' },
      { key: 'status', label: 'Quote Status', sortable: true, format: 'status' },
      { key: 'created_at', label: 'Date Created', sortable: true, format: 'date' },
      { key: 'salesperson', label: 'Salesperson', sortable: true },
    ],
    dataSource: 'quotes',
    computeKpis: (data) => {
      const customers = new Set(data.map(d => d.customer_name || d.customer_id)).size;
      const totalValue = data.reduce((sum, d) => sum + (d.total || d.amount || 0), 0);
      return {
        total_customers: customers,
        total_quotes: data.length,
        total_value: totalValue,
        avg_quote: data.length > 0 ? Math.round(totalValue / data.length) : 0,
      };
    },
    computeRow: (item) => ({
      ...item,
      amount: item.total || item.amount || 0,
      created_at: quoteDateValue(item), // IDI-777: the Quote Date; the API sends camelCase
      salesperson: item.salesperson || item.created_by || '—',
    }),
  },

  // ─── B3: Quotes by Lead Source ───────────────────────────────────────────────
  quotes_by_lead_source: {
    id: 'quotes_by_lead_source',
    title: 'Quotes by Lead Source',
    description: 'Analyze quote volume and value by lead source.',
    category: 'sales',
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'lead_source', type: 'select', label: 'Lead Source', options: ['all', 'referral', 'repeat_customer', 'internet', 'facebook', 'craigslist', 'angis_list', 'other'] },
    ],
    kpis: [
      { key: 'total_quotes', label: 'Total Quotes', format: 'number' },
      { key: 'total_value', label: 'Total Value', format: 'currency' },
      { key: 'top_source', label: 'Top Source', format: 'text' },
      { key: 'avg_margin', label: 'Avg Margin %', format: 'percent' },
    ],
    columns: [
      { key: 'quote_number', label: 'Quote Name', sortable: true },
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'lead_source', label: 'Lead Source', sortable: true },
      { key: 'status', label: 'Quote Status', sortable: true, format: 'status' },
      { key: 'created_at', label: 'Date Created', sortable: true, format: 'date' },
      { key: 'salesperson', label: 'Salesperson', sortable: true },
      { key: 'amount', label: 'Quote Amount', sortable: true, align: 'right', format: 'currency' },
      { key: 'margin_pct', label: 'Gross Margin %', sortable: true, align: 'right', format: 'percent' },
    ],
    dataSource: 'quotes',
    computeKpis: (data) => {
      const totalValue = data.reduce((sum, d) => sum + (d.total || d.amount || 0), 0);
      const sources = {};
      data.forEach(d => { const s = d.lead_source || 'Unknown'; sources[s] = (sources[s] || 0) + 1; });
      const topSource = Object.entries(sources).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
      const margins = data.filter(d => (d.total || d.amount) > 0).map(d => {
        const r = d.total || d.amount || 0;
        const c = d.cost || 0;
        return r > 0 ? ((r - c) / r) * 100 : 0;
      });
      const avgMargin = margins.length > 0 ? Math.round(margins.reduce((a, b) => a + b, 0) / margins.length) : 0;
      return { total_quotes: data.length, total_value: totalValue, top_source: topSource, avg_margin: avgMargin };
    },
    computeRow: (item) => ({
      ...item,
      amount: item.total || item.amount || 0,
      created_at: quoteDateValue(item), // IDI-777: the Quote Date; the API sends camelCase
      salesperson: item.salesperson || item.created_by || '—',
      lead_source: item.lead_source || '—',
      margin_pct: (item.total || item.amount) > 0 ? Math.round((((item.total || item.amount) - (item.cost || 0)) / (item.total || item.amount)) * 100) : 0,
    }),
  },

  // ─── B4: Lost Quotes Analysis ────────────────────────────────────────────────
  lost_quotes: {
    id: 'lost_quotes',
    title: 'Lost Quotes Analysis',
    description: 'Analyze declined/lost quotes by reason and salesperson.',
    category: 'sales',
    // B-F5: Grouped subtotals by category
    groupBy: 'job_type',
    groupSubtotals: true,
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'salesperson', type: 'text', label: 'Salesperson' },
    ],
    kpis: [
      { key: 'total_lost', label: 'Total Lost', format: 'number' },
      { key: 'lost_value', label: 'Lost Value', format: 'currency' },
      { key: 'top_reason', label: 'Top Reason', format: 'text' },
      { key: 'loss_rate', label: 'Loss Rate', format: 'percent' },
    ],
    columns: [
      { key: 'lost_reason', label: 'Lost Reason', sortable: true },
      { key: 'created_at', label: 'Created', sortable: true, format: 'date' },
      { key: 'salesperson', label: 'Salesperson', sortable: true },
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'title', label: 'Project Name', sortable: true },
      { key: 'job_type', label: 'Job Type', sortable: true },
      { key: 'amount', label: 'Job $', sortable: true, align: 'right', format: 'currency' },
      { key: 'margin_pct', label: 'Margin %', sortable: true, align: 'right', format: 'percent' },
    ],
    dataSource: 'quotes',
    // Pre-filter to only declined/lost quotes
    preFilter: (data) => data.filter(d => d.status === 'declined' || d.status === 'lost' || d.status === 'expired'),
    computeKpis: (data) => {
      const lost = data.filter(d => d.status === 'declined' || d.status === 'lost' || d.status === 'expired');
      const lostValue = lost.reduce((sum, d) => sum + (d.total || d.amount || 0), 0);
      const reasons = {};
      lost.forEach(d => { const r = d.lost_reason || d.decline_reason || 'Unknown'; reasons[r] = (reasons[r] || 0) + 1; });
      const topReason = Object.entries(reasons).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
      return {
        total_lost: lost.length,
        lost_value: lostValue,
        top_reason: topReason,
        loss_rate: data.length > 0 ? Math.round((lost.length / data.length) * 100) : 0,
      };
    },
    computeRow: (item) => ({
      ...item,
      amount: item.total || item.amount || 0,
      created_at: quoteDateValue(item), // IDI-777: the Quote Date; the API sends camelCase
      salesperson: item.salesperson || item.created_by || '—',
      lost_reason: item.lost_reason || item.decline_reason || 'Unknown',
      job_type: item.job_type || item.category || '—',
      margin_pct: (item.total || item.amount) > 0 ? Math.round((((item.total || item.amount) - (item.cost || 0)) / (item.total || item.amount)) * 100) : 0,
    }),
  },

  // ─── B6: Inventory Report ────────────────────────────────────────────────────
  inventory: {
    id: 'inventory',
    title: 'Inventory Report',
    description: 'Current inventory levels by item type and warehouse.',
    category: 'operations',
    filters: [
      { key: 'status', type: 'select', label: 'Status', options: ['active', 'all', 'inactive'] },
    ],
    kpis: [
      { key: 'total_items', label: 'Total Items', format: 'number' },
      { key: 'total_available', label: 'Available Qty', format: 'number' },
      { key: 'total_allocated', label: 'Allocated Qty', format: 'number' },
      { key: 'item_types', label: 'Item Types', format: 'number' },
    ],
    columns: [
      { key: 'item_type', label: 'Item Type', sortable: true },
      { key: 'item_name', label: 'Item Name', sortable: true },
      { key: 'vendor', label: 'Vendor', sortable: true },
      { key: 'warehouse', label: 'Warehouse', sortable: true },
      { key: 'available_qty', label: 'Available Qty', sortable: true, align: 'right' },
      { key: 'allocated_qty', label: 'Allocated Qty', sortable: true, align: 'right' },
    ],
    dataSource: 'jobs', // Placeholder — inventory data would come from a dedicated endpoint
    computeKpis: (data) => ({
      total_items: data.length,
      total_available: data.reduce((sum, d) => sum + (d.available_qty ?? 0), 0),
      total_allocated: data.reduce((sum, d) => sum + (d.allocated_qty ?? 0), 0),
      item_types: new Set(data.map(d => d.item_type)).size,
    }),
    computeRow: (item) => ({ ...item }),
  },

  // ─── B7: Payroll Summary Report ──────────────────────────────────────────────
  payroll_summary: {
    id: 'payroll_summary',
    title: 'Payroll Summary',
    description: 'Payroll costs by technician with hours and labor rates.',
    category: 'operations',
    visibility: 'admin', // LAT-25: admin/owner only
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
    ],
    kpis: [
      { key: 'total_technicians', label: 'Technicians', format: 'number' },
      { key: 'total_hours', label: 'Total Hours', format: 'number' },
      { key: 'total_labor_cost', label: 'Total Labor Cost', format: 'currency' },
      { key: 'avg_rate', label: 'Avg Rate', format: 'currency' },
    ],
    columns: [
      { key: 'technician', label: 'Technician', sortable: true },
      { key: 'date', label: 'Date', sortable: true, format: 'date' },
      { key: 'hours', label: 'Hours', sortable: true, align: 'right' },
      { key: 'rate', label: 'Rate', sortable: true, align: 'right', format: 'currency' },
      { key: 'labor_cost', label: 'Labor Cost', sortable: true, align: 'right', format: 'currency' },
    ],
    dataSource: 'jobs', // Placeholder — payroll data would come from a dedicated endpoint
    computeKpis: (data) => {
      const technicians = new Set(data.map(d => d.technician || d.crew || d.assigned_to).filter(Boolean)).size;
      const totalHours = data.reduce((sum, d) => sum + (d.hours ?? 0), 0);
      const totalCost = data.reduce((sum, d) => sum + (d.labor_cost || 0), 0);
      return {
        total_technicians: technicians,
        total_hours: totalHours,
        total_labor_cost: totalCost,
        avg_rate: totalHours > 0 ? Math.round(totalCost / totalHours) : 0,
      };
    },
    computeRow: (item) => ({
      ...item,
      technician: item.technician || item.crew || item.assigned_to || '—',
    }),
  },

  // ─── Legacy aliases (keep for backward compat) ───────────────────────────────
  job_status: {
    id: 'job_status',
    title: 'Job Status',
    description: 'Overview of all jobs by status, crew assignment, and schedule.',
    category: 'operations',
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'status', type: 'select', label: 'Status', match: 'exact', options: ['all', 'scheduled', 'partially_scheduled', 'completed', 'not_scheduled'] },
      { key: 'crew', type: 'text', label: 'Crew' },
    ],
    kpis: [
      { key: 'total_jobs', label: 'Total Jobs', format: 'number' },
      { key: 'active', label: 'Active', format: 'number' },
      { key: 'completed', label: 'Completed', format: 'number' },
      { key: 'completion_rate', label: 'Completion Rate', format: 'percent' },
    ],
    columns: [
      { key: 'title', label: 'Job Title', sortable: true },
      { key: 'customer_name', label: 'Customer', sortable: true },
      { key: 'status', label: 'Status', sortable: true, format: 'status' },
      { key: 'crew', label: 'Crew', sortable: true },
      { key: 'scheduled_date', label: 'Scheduled', sortable: true, format: 'date' },
    ],
    dataSource: 'jobs',
    preFilter: withDerivedJobStatus,
    chartType: 'donut',
    chartConfig: {
      groupBy: 'status',
      xKey: 'label',
      yKey: 'value',
      yLabel: 'Jobs',
    },
    computeKpis: (data) => {
      const total = data.length;
      const active = countActiveJobs(data);
      const completed = data.filter(d => d.status === 'completed').length;
      return {
        total_jobs: total,
        active,
        completed,
        completion_rate: total > 0 ? Math.round((completed / total) * 100) : 0,
      };
    },
    computeRow: (item) => ({ ...item }),
  },

  // B-F1: Margin Trend Report (Iter 9)
  margin_trend: {
    id: 'margin_trend',
    title: 'Margin Trend',
    description: 'Trends realized gross margin over time. Consumes blended margin from the margin engine (M-7).',
    category: 'financial',
    filters: [
      { key: 'date_range', type: 'date_range', label: 'Date Range' },
      { key: 'job_type', type: 'select', label: 'Job Type', options: ['All', 'Insulation', 'Fireproofing', 'Firestopping', 'Acoustical', 'Spray Foam', 'Other'] },
    ],
    kpis: [
      { key: 'avg_margin', label: 'Avg Margin', format: 'percent' },
      { key: 'margin_trend_dir', label: 'Trend', format: 'text' },
      { key: 'total_revenue', label: 'Total Revenue', format: 'currency' },
      { key: 'total_profit', label: 'Total Profit', format: 'currency' },
    ],
    columns: [
      { key: 'period', label: 'Period', sortable: true },
      { key: 'revenue', label: 'Revenue', sortable: true, align: 'right', format: 'currency' },
      { key: 'cost', label: 'Cost', sortable: true, align: 'right', format: 'currency' },
      { key: 'profit', label: 'Profit', sortable: true, align: 'right', format: 'currency' },
      { key: 'margin_pct', label: 'Margin %', sortable: true, align: 'right', format: 'percent' },
      { key: 'job_count', label: 'Jobs', sortable: true, align: 'right' },
    ],
    dataSource: 'jobs',
    chartType: 'line',
    chartConfig: { xKey: 'period', yKey: 'margin_pct', label: 'Realized Gross Margin %' },
    computeKpis: (data) => {
      const totalRevenue = data.reduce((sum, d) => sum + (d.total || d.revenue || 0), 0);
      const totalCost = data.reduce((sum, d) => sum + (d.cost || 0), 0);
      const avgMargin = totalRevenue > 0 ? Math.round(((totalRevenue - totalCost) / totalRevenue) * 100) : 0;
      return { avg_margin: avgMargin, margin_trend_dir: 'Stable', total_revenue: totalRevenue, total_profit: totalRevenue - totalCost };
    },
    computeRow: (item) => ({
      ...item,
      revenue: item.total || item.revenue || 0,
      cost: item.cost || 0,
      profit: (item.total || item.revenue || 0) - (item.cost || 0),
      margin_pct: (item.total || item.revenue) > 0 ? Math.round((((item.total || item.revenue) - (item.cost || 0)) / (item.total || item.revenue)) * 100) : 0,
      job_count: item.job_count || 1,
    }),
  },
};

// Slicer definitions per report — REPORTS_DETAIL_SPEC §Div-3
export const REPORT_SLICERS = {
  revenue: ['By Month', 'By Job Type', 'By Salesperson'],
  margin_analysis: ['By Month', 'By Job Type', 'By Salesperson'],
  job_performance: ['By Month', 'By Job Type', 'By Salesperson'],
  quote_performance: ['By Month', 'By Salesperson'],
  bid_log: ['By Month', 'By Job Type', 'By Salesperson'],
  customer_quote_contact: ['By Month', 'By Salesperson'],
  lost_quotes: ['By Month', 'By Job Type', 'By Salesperson'],
  job_status: ['By Month', 'By Job Type'],
  margin_trend: ['By Month', 'By Job Type'],
  payroll_summary: ['By Month'],
  inventory: [],
  quotes_by_lead_source: ['By Month', 'By Salesperson'],
};

// Categorized for the library view — per REPORT_DETAIL_SPECS.md
export const REPORT_CATEGORIES = [
  {
    id: 'financial',
    label: 'Financial',
    reports: ['revenue', 'margin_analysis', 'margin_trend', 'job_performance'],
  },
  {
    id: 'operations',
    label: 'Operations',
    reports: ['job_status', 'inventory', 'payroll_summary'],
  },
  {
    id: 'sales',
    label: 'Sales',
    reports: ['bid_log', 'quote_performance', 'customer_quote_contact', 'quotes_by_lead_source', 'lost_quotes'],
  },
  {
    id: 'communications',
    label: 'Communications',
    reports: [],
  },
];
